import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import Form from '../../../../../../components/Form'
import LocationListFormFields from '../../LocationListFormFields'

import useManageDrawer from '../../../../../../hooks/formHooks/useManageDrawer'

import handleAddressesErrors from '../../helpers'

import {
  validateLocationAddresses,
  clearValidateLocationAddresses,
  updateLocationList,
  clearUpdateLocationList
} from '../../../../../../modules/actions/location'
import { selectedSelfAccountSelector } from '../../../../../../modules/selectors/app'
import {
  updateLocationListLoadingSelector,
  updateLocationListErrorSelector,
  locationAddressesIsValidationSuccessSelector,
  locationAddressesValidationErrorSelector,
  locationAddressesValidationLoadingSelector,
  locationAddressesValidationSelector,
  locationListSelector,
  updateLocationListWasUpdatedSelector
} from '../../../../../../modules/selectors/location'
import { selectedPartnershipIdSelector } from '../../../../../../modules/selectors/partnerships'

import {
  VALIDATION_ERROR_CODE,
  getInitialValues,
  validationSchema,
  transformAddressesValidationFieldsToBE,
  transformLocationListFieldsToBE
} from '../../fields'

import { LOCATION_LIST_EDIT } from '../../../../../../constants/forms'
import useDrawerFormStyles from '../../../../../../styles/common/drawerForms'

const LocationListEditForm = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const drawerFormClasses = useDrawerFormStyles()

  const [addressesErrorIndexes, setAddressesErrorIndexes] = useState([])

  const updateLocationListWasUpdated = useSelector(updateLocationListWasUpdatedSelector)
  const selectedSelfAccount = useSelector(selectedSelfAccountSelector)
  const locationList = useSelector(locationListSelector)
  const locationAddressesValidation = useSelector(locationAddressesValidationSelector)
  const locationAddressesValidationLoading = useSelector(locationAddressesValidationLoadingSelector)
  const locationAddressesValidationError = useSelector(locationAddressesValidationErrorSelector)
  const locationAddressesIsValidationSuccess = useSelector(locationAddressesIsValidationSuccessSelector)

  const selectedPartnershipId = useSelector(selectedPartnershipIdSelector)

  const { selectedEditItemId } = useManageDrawer({
    formName: LOCATION_LIST_EDIT
  })

  const isLocationAddressesValidationError =
    locationAddressesValidationError && locationAddressesValidationError.code === VALIDATION_ERROR_CODE

  const clearUpdateLocationsSubmit = useCallback(() => {
    dispatch(clearUpdateLocationList())
  }, [dispatch])

  const onSubmit = useCallback(
    values => {
      // reset addressesErrorIndexes and clear redux state before doing new validation request
      // if there was already an error before
      if (isLocationAddressesValidationError) {
        dispatch(clearValidateLocationAddresses())
        setAddressesErrorIndexes([])
      }

      dispatch(validateLocationAddresses(transformAddressesValidationFieldsToBE(values)))
    },
    [dispatch, isLocationAddressesValidationError]
  )

  const initialValues = useMemo(() => getInitialValues(locationList), [locationList])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  })

  const { values } = formik
  const { addresses } = values

  useEffect(() => {
    if (!locationAddressesIsValidationSuccess && isLocationAddressesValidationError && !addressesErrorIndexes.length) {
      handleAddressesErrors(addresses, locationAddressesValidation, setAddressesErrorIndexes)
    }
  }, [
    locationAddressesIsValidationSuccess,
    isLocationAddressesValidationError,
    locationAddressesValidation,
    addresses,
    addressesErrorIndexes
  ])

  useEffect(() => {
    if (locationAddressesIsValidationSuccess && selectedEditItemId) {
      const formattedValues = transformLocationListFieldsToBE(values, locationAddressesValidation)

      dispatch(updateLocationList(formattedValues, selectedEditItemId))
    }
  }, [
    dispatch,
    locationAddressesIsValidationSuccess,
    values,
    locationAddressesValidation,
    selectedSelfAccount,
    selectedEditItemId,
    selectedPartnershipId
  ])

  return (
    <>
      <Form
        formName={LOCATION_LIST_EDIT}
        formik={formik}
        successSubmit={updateLocationListWasUpdated}
        errorSelector={
          locationAddressesIsValidationSuccess
            ? updateLocationListErrorSelector
            : locationAddressesValidationErrorSelector
        }
        isLoadingSelector={
          locationAddressesIsValidationSuccess
            ? updateLocationListLoadingSelector
            : locationAddressesValidationLoadingSelector
        }
        clearHandler={clearUpdateLocationsSubmit}
      >
        <LocationListFormFields formik={formik} addressesErrorIndexes={addressesErrorIndexes} isEditForm />
      </Form>
      {addresses.length > 10 && locationAddressesValidationLoading ? (
        <div className={drawerFormClasses.afterFormText}>
          {t(
            'It can take a few minutes to match the addresses. Please leave this page open while this process is running'
          )}{' '}
        </div>
      ) : null}
    </>
  )
}

export default LocationListEditForm
